import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import {isEmpty} from 'lodash';
import {ApplicationName} from 'src/js/common/types';
import {I18N_REDUCER_STATE_ID} from 'src/js/containers/i18n/i18n_constants';
import * as at from 'src/js/containers/login/login_action_types';
import {LOGIN_REDUCER_STATE_ID} from 'src/js/containers/login/login_constants';
import {AppStore} from 'src/js/store';

import {FetcherUtils} from './fetcher_utils';
import {getAccessTokenValue} from './security_utils';

export interface IApiClient {
  _store?: AppStore;
  _onUnauthorizedThunk?: any;
  _authorizedAxiosInstance?: AxiosInstance;
  _unAuthorizedAxiosInstance?: AxiosInstance;
  initApiClient: (applicationName: ApplicationName, newStore: AppStore, isAuthorized?: boolean, onUnauthorizedThunk?: any) => void;
  getInstance: (isAuth: boolean) => AxiosInstance;
  getData: <T = any, D = any>(config: AxiosRequestConfig<D>, isAuthorized?: boolean, axiosInstance?: AxiosInstance) => Promise<T>;
}
export type TypeWithEtag<T extends object> = T & {
  _etag?: string;
};

const XSRF_TOKEN = 'XSRF-TOKEN';
const X_XSRF_TOKEN_HEADER = 'X-XSRF-TOKEN';
const AUTHORIZATION_HEADER = 'Authorization';
const ACCEPT_LANGUAGE = 'Accept-Language';
const APPLICATION_NAME = 'ApplicationName';

export const ApiClient: IApiClient = {
  _store: undefined,
  _onUnauthorizedThunk: undefined,
  _authorizedAxiosInstance: undefined,
  _unAuthorizedAxiosInstance: undefined,
  initApiClient: (applicationName, store, isAuthorized = true, onUnauthorizedThunk = undefined) => {
    const axiosApiInstance = axios.create();
    // Request interceptor for API calls
    axiosApiInstance.interceptors.request.use(
      async (config) => {
        const enhancedConfig = {...config};

        // add ApplicationName header
        enhancedConfig.headers = {
          ...enhancedConfig.headers,
          [APPLICATION_NAME]: applicationName,
        };

        // TODO: check if store is the current one
        if (ApiClient._store?.getState()) {
          const state = ApiClient._store.getState();
          // handle 'Accept-Language' header
          enhancedConfig.headers = {
            ...enhancedConfig.headers,
            [ACCEPT_LANGUAGE]: state[I18N_REDUCER_STATE_ID].currentLocale,
          };

          // reset user idle time
          ApiClient._store.dispatch({
            type: at.RESET_IDLE_TIME,
            payload: true,
            stateId: LOGIN_REDUCER_STATE_ID,
          });
        }

        if (isAuthorized) {
          // handle 'Authorization' header
          const accessToken = getAccessTokenValue() || '';
          if (!isEmpty(accessToken)) {
            enhancedConfig.headers = {
              ...enhancedConfig.headers,
              [AUTHORIZATION_HEADER]: accessToken,
            };
          }
        }

        const csrfToken = FetcherUtils.getCookieValue(XSRF_TOKEN);
        if (!isEmpty(csrfToken)) {
          // handle CSRF token header
          enhancedConfig.headers = {
            ...enhancedConfig.headers,
            [X_XSRF_TOKEN_HEADER]: csrfToken,
          };
        }

        if (!isEmpty(enhancedConfig.headers)) {
          // set credentials
          enhancedConfig.withCredentials = true;
        }
        return enhancedConfig;
      },
      (error) => {
        Promise.reject(error);
      }
    );

    // Response interceptor for API calls
    axiosApiInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (
          isAuthorized &&
          (error?.response === undefined || error?.response?.status === 401) &&
          ApiClient._store &&
          ApiClient._onUnauthorizedThunk
        ) {
          ApiClient._store.dispatch<any>(ApiClient._onUnauthorizedThunk());
        }
        return Promise.reject(error);
      }
    );

    if (isAuthorized) {
      ApiClient._authorizedAxiosInstance = axiosApiInstance;
    } else {
      ApiClient._unAuthorizedAxiosInstance = axiosApiInstance;
      ApiClient._onUnauthorizedThunk = onUnauthorizedThunk;
    }
    ApiClient._store = store;
  },

  getInstance: (isAuthorized = true): AxiosInstance =>
    isAuthorized && ApiClient._authorizedAxiosInstance ? ApiClient._authorizedAxiosInstance : ApiClient._unAuthorizedAxiosInstance ?? axios,

  getData: async <T = any, D = any>(config: AxiosRequestConfig<D>, isAuthorized, axiosInstance): Promise<T> => {
    let data: T;
    const axiosInstanceCurrent = axiosInstance ?? ApiClient.getInstance(isAuthorized);
    return axiosInstanceCurrent(config)
      .then((response: AxiosResponse<T, D>) => {
        data = response.data;
        const {'content-type': contentType, 'etag': etagRaw} = response.headers;
        const etag = etagRaw && etagRaw.startsWith('"') ? JSON.parse(etagRaw) : etagRaw; // NOTE: copy-paste from StringUtils.parseEtag()
        if (contentType === 'application/json' && etag) {
          const dataObject = data as any;
          dataObject._etag = etag;
          return dataObject;
        }
        return data;
      })
      .catch((e) => {
        return Promise.reject(e);
      });
  },
};
